import {
    Box,
    VStack,
    HStack,
    Heading,
    Text,
    useBreakpointValue,
    Container,
    Flex,
    Link,
    Image,
    Divider,
    IconButton,
    Stack,
    Button, useDisclosure,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    useToast,
} from '@chakra-ui/react'
import React, { useEffect, useState, useMemo } from "react";
import FullScreenTemplate from "../../templates/FullScreenTemplate";
import LargeWithNewsletter from "../../components/Reuseable/LargeWithNewsletter";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FaTwitter, FaLinkedin, FaFacebook, FaLink } from 'react-icons/fa';
import './blog-styles.css';
import SignUpScreen1 from "../../components/Modals/SignUp/SignUpScreen1";
import {useNavigate} from "react-router-dom";

// Table of Contents Component
const TableOfContents = ({ headings, activeId }) => {
    const toast = useToast();
    
    const handleScrollToSection = (id) => {
        const element = document.getElementById(id);
        if (element) {
            const navbarHeight = 110; // Account for navbar height + extra buffer
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - navbarHeight;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
        }
    };

    return (
        <Box
            position="sticky"
            top="88px"
            bg="white"
            p={6}
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
            shadow="sm"
            zIndex={1000}
            maxHeight="calc(100vh - 40px)"
            overflowY="auto"
        >
            <Heading size="sm" mb={4} color="gray.700">
                On this page
            </Heading>
            <VStack align="stretch" spacing={2}>
                {headings.map((heading, index) => (
                    <Link
                        key={index}
                        onClick={(e) => {
                            e.preventDefault();
                            handleScrollToSection(heading.id);
                        }}
                        fontSize="sm"
                        color={activeId === heading.id ? "blue.600" : "gray.600"}
                        fontWeight={activeId === heading.id ? "semibold" : "normal"}
                        _hover={{ color: "blue.600", cursor: "pointer" }}
                        pl={heading.level > 2 ? (heading.level - 2) * 4 : 0}
                        transition="all 0.2s"
                    >
                        {heading.text}
                    </Link>
                ))}
            </VStack>
            
            <Divider my={4} />
            
            <Box>
                <Text fontSize="sm" fontWeight="semibold" mb={3} color="gray.700">
                    Share article
                </Text>
                <HStack spacing={2}>
                    <IconButton
                        aria-label="Share on Twitter"
                        icon={<FaTwitter />}
                        size="sm"
                        colorScheme="twitter"
                        variant="ghost"
                        onClick={() => {
                            const url = window.location.href;
                            const text = "Check out this article:";
                            window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
                        }}
                    />
                    <IconButton
                        aria-label="Share on LinkedIn"
                        icon={<FaLinkedin />}
                        size="sm"
                        colorScheme="linkedin"
                        variant="ghost"
                        onClick={() => {
                            const url = window.location.href;
                            window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank');
                        }}
                    />
                    <IconButton
                        aria-label="Share on Facebook"
                        icon={<FaFacebook />}
                        size="sm"
                        colorScheme="facebook"
                        variant="ghost"
                        onClick={() => {
                            const url = window.location.href;
                            window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                        }}
                    />
                    <IconButton
                        aria-label="Copy link"
                        icon={<FaLink />}
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                            navigator.clipboard.writeText(window.location.href);
                            toast({
                                title: "Link copied!",
                                status: "success",
                                duration: 4000,
                                isClosable: true,
                                position: "bottom",
                            });
                        }}
                    />
                </HStack>
            </Box>
        </Box>
    );
};

// Custom markdown components
const MarkdownComponents = {
    h1: ({ children, ...props }) => {
        const id = children.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-');
        return <Heading as="h1" size="xl" id={id} {...props}>{children}</Heading>;
    },
    h2: ({ children, ...props }) => {
        const id = children.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-');
        return <Heading as="h2" size="lg" id={id} {...props}>{children}</Heading>;
    },
    h3: ({ children, ...props }) => {
        const id = children.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-');
        return <Heading as="h3" size="md" id={id} {...props}>{children}</Heading>;
    },
    h4: ({ children, ...props }) => {
        const id = children.toString().toLowerCase().replace(/[^a-z0-9]+/g, '-');
        return <Heading as="h4" size="sm" id={id} {...props}>{children}</Heading>;
    },
    img: ({ src, alt, ...props }) => (
        <Image
            src={src || "/document.svg"}
            alt={alt}
            borderRadius="lg"
            shadow="md"
            my={6}
            maxW="100%"
            {...props}
        />
    ),
    table: ({ children, ...props }) => (
        <Table {...props}>{children}</Table>
    ),
    thead: ({ children, ...props }) => <Thead {...props}>{children}</Thead>,
    tbody: ({ children, ...props }) => <Tbody {...props}>{children}</Tbody>,
    tr: ({ children, ...props }) => <Tr {...props}>{children}</Tr>,
    th: ({ children, ...props }) => <Th {...props}>{children}</Th>,
    td: ({ children, ...props }) => <Td {...props}>{children}</Td>,
    iframe: ({ src, width, height, style, ...props }) => (
        <Box
            as="iframe"
            src={src}
            width={width || "100%"}
            height={height || "400px"}
            style={style}
            borderRadius="lg"
            shadow="md"
            my={6}
            allow="autoplay; encrypted-media"
            allowFullScreen
            {...props}
        />
    ),
};

// Function to extract iframes from content and render them
const renderContentWithIframes = (content) => {
    // Split content by iframe tags
    const iframeRegex = /<iframe[^>]*src="([^"]*)"[^>]*>/gi;
    const parts = content.split(iframeRegex);
    
    if (parts.length === 1) {
        // No iframes found, render as normal markdown
        return <ReactMarkdown remarkPlugins={[remarkGfm]} components={MarkdownComponents}>{content}</ReactMarkdown>;
    }
    
    const elements = [];
    for (let i = 0; i < parts.length; i++) {
        if (i % 2 === 0) {
            // This is markdown content
            if (parts[i].trim()) {
                elements.push(
                    <ReactMarkdown key={`md-${i}`} remarkPlugins={[remarkGfm]} components={MarkdownComponents}>
                        {parts[i]}
                    </ReactMarkdown>
                );
            }
        } else {
            // This is an iframe src
            const iframeSrc = parts[i];
            elements.push(
                <Box
                    key={`iframe-${i}`}
                    as="iframe"
                    src={iframeSrc}
                    width="100%"
                    height="400px"
                    borderRadius="lg"
                    shadow="md"
                    my={6}
                    allow="autoplay; encrypted-media"
                    allowFullScreen
                    style={{ aspectRatio: '16 / 9' }}
                />
            );
        }
    }
    
    return elements;
};

export default function BlogPost({ title, subtitle, publishDate, author, content, metaDescription, keywords }) {
    const breakpoint = useBreakpointValue({ base: 'base', sm: 'sm', md: 'md', lg: 'lg', xl: 'xl' });
    const isMobile = breakpoint === 'base' || breakpoint === 'sm';
    const [activeId, setActiveId] = useState('');
    const { isOpen: isOpenSignUpScreen1, onOpen: onOpenSignUpScreen1, onClose: onCloseSignUpScreen1 } = useDisclosure();
    const { isOpen: isOpenSignInScreen1, onOpen: onOpenSignInScreen1, onClose: onCloseSignInScreen1 } = useDisclosure();
    const navigate = useNavigate();

    // Extract headings from content for table of contents
    const headings = useMemo(() => {
        const extractedHeadings = [];
        
        // Split content into lines and look for markdown headings
        const lines = content.split('\n');
        
        lines.forEach(line => {
            // Look for ## headings (h2) only
            const match = line.match(/^##\s+(.+)/);
            if (match) {
                const text = match[1];
                const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                extractedHeadings.push({ level: 2, text, id });
            }
        });
        
        return extractedHeadings;
    }, [content]);

    useEffect(() => {
        window.scrollTo(0, 0);
        
        // Set up intersection observer for active heading tracking
        const observer = new IntersectionObserver(
            (entries) => {
                const visibleHeadings = entries
                    .filter((entry) => entry.isIntersecting)
                    .map((entry) => entry.target.id);
                
                if (visibleHeadings.length > 0) {
                    setActiveId(visibleHeadings[0]);
                }
            },
            { rootMargin: '-100px 0px -80% 0px' }
        );

        // Observe all headings
        headings.forEach((heading) => {
            const element = document.getElementById(heading.id);
            if (element) observer.observe(element);
        });

        return () => observer.disconnect();
    }, [headings]);

    // SEO meta tags
    useEffect(() => {
        if (metaDescription) {
            document.querySelector('meta[name="description"]')?.setAttribute('content', metaDescription);
        }
        if (keywords) {
            document.querySelector('meta[name="keywords"]')?.setAttribute('content', keywords);
        }
        document.title = `${title} | Honeybear AI`;
    }, [title, metaDescription, keywords]);

    return (
        <div>
            <FullScreenTemplate>
                <Box maxW="7xl" mx="auto" px={4} py={10}>
                    {/* Blog Header */}
                    <VStack spacing={6} align="stretch" mb={12}>
                        <Heading 
                            as="h1" 
                            fontSize={isMobile ? "3xl" : "5xl"} 
                            fontWeight="bold"
                            lineHeight="shorter"
                            textAlign="left"
                        >
                            {title}
                        </Heading>
                        {subtitle && (
                            <Text 
                                fontSize={isMobile ? "lg" : "xl"} 
                                color="gray.600"
                                lineHeight="1.6"
                                textAlign="left"
                            >
                                {subtitle}
                            </Text>
                        )}
                        <Stack direction={isMobile ? "column" : "row"} spacing={4} align={isMobile ? "stretch" : "center"}>
                            {author && (
                                <Text fontSize="md" color="gray.500" textAlign="left">
                                    By <Text as="span" fontWeight="semibold">{author}</Text>
                                </Text>
                            )}
                            {publishDate && (
                                <>
                                    {!isMobile && <Text color="gray.300">•</Text>}
                                    <Text fontSize="md" color="gray.500" textAlign="left">
                                        {publishDate}
                                    </Text>
                                </>
                            )}
                        </Stack>
                    </VStack>

                    {/* Two Column Layout */}
                    {isMobile ? (
                        <Box>
                            <Box className="blog-content">
                                {renderContentWithIframes(content)}
                            </Box>
                            
                            {/* Call-to-Action Sign Up Button - Mobile */}
                            <Box mt={6}>
                                <Button
                                    borderRadius={16}
                                    colorScheme={'orange'}
                                    bgGradient="linear(to-r, orange.400, orange.500)"
                                    _hover={{
                                        bgGradient: "linear(to-r, orange.400, orange.600)",
                                        boxShadow: 'lg'
                                    }}
                                    // new stuff above
                                    size="lg"
                                    fontWeight="bold"
                                    px={8}
                                    _active={{
                                        transform: "translateY(0px)",
                                    }}
                                    transition="all 0.3s ease"
                                    onClick={() => {
                                        navigate('/');
                                    }}
                                >
                                    Sign Up Today
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Box display="grid" gridTemplateColumns="1fr 320px" gap={8}>
                            {/* Main Content Column */}
                            <Box>
                                <Box className="blog-content">
                                    {renderContentWithIframes(content)}
                                </Box>
                                
                                {/* Call-to-Action Sign Up Button */}
                                <Box mt={8}>
                                    <Button
                                        borderRadius={16}
                                        colorScheme={'orange'}
                                        bgGradient="linear(to-r, orange.400, orange.500)"
                                        _hover={{
                                            bgGradient: "linear(to-r, orange.400, orange.600)",
                                            boxShadow: 'lg'
                                        }}
                                        // new stuff above
                                        size="lg"
                                        fontWeight="bold"
                                        px={8}
                                        _active={{
                                            transform: "translateY(0px)",
                                        }}
                                        transition="all 0.3s ease"
                                        onClick={() => {
                                            navigate('/');
                                        }}
                                    >
                                        Sign Up Today
                                    </Button>
                                </Box>
                            </Box>

                            {/* Sidebar */}
                            <Box>
                                <TableOfContents headings={headings} activeId={activeId} />
                            </Box>
                        </Box>
                    )}
                </Box>
                
                <Box height="auto" width="100%" mt={16}>
                    <LargeWithNewsletter />
                </Box>
            </FullScreenTemplate>
            <SignUpScreen1 isOpenSignUpScreen1={isOpenSignUpScreen1} onCloseSignUpScreen1={onCloseSignUpScreen1} onOpenSignInScreen1={onOpenSignInScreen1}/>
        </div>
    )
} 
